export interface WorkspaceSearch {
  room?: string;
  visit?: string;
  subject?: string;
  view?: string;
}

const str = (v: unknown): string | undefined =>
  typeof v === "string" && v.length > 0 ? v : undefined;

/**
 * Coerces the `/` route's raw search into the typed workspace selection. Room,
 * visit, subject, and the viewed SavedView all live here so the workspace view
 * is linkable and survives back/forward - see `useWorkspaceNavigation` for the
 * writes and `resolveSelection` for how they resolve to one on-screen thing.
 */
export const validateWorkspaceSearch = (
  search: Record<string, unknown>,
): WorkspaceSearch => ({
  room: str(search.room),
  visit: str(search.visit),
  subject: str(search.subject),
  view: str(search.view),
});

/**
 * The one thing the workspace is showing, resolved from the four `/` route
 * search params. A discriminated union rather than four optional fields, so
 * every consumer (the workspace render, the derived active subject) switches
 * on `mode` instead of re-deriving which param wins.
 */
export type WorkspaceSelection =
  | { mode: "clinic" }
  | { mode: "room"; roomUuid: string }
  | { mode: "visit"; visitUuid: string; roomUuid: string | null }
  | { mode: "subject"; subjectId: string }
  | { mode: "view"; viewId: string };

/**
 * Resolves the raw search params to a single selection by precedence. The one place
 * that rule lives — both the workspace render and `useActiveSubject` call it, so
 * they can never disagree about what's on screen.
 *
 * Highest first: `view` (a top-level overlay) > `subject` > `visit` (a selected
 * patient-flow card) > `room` > `clinic` (nothing selected, the rooms grid).
 *
 * `openSubject` clears room/visit, so a subject coexisting with them only arises
 * from a hand-edited URL; the precedence resolves that deterministically anyway.
 * Visit mode carries its room along rather than requiring a second lookup, since
 * `selectVisit` always sets them atomically.
 */
export const resolveSelection = (
  search: WorkspaceSearch,
): WorkspaceSelection => {
  if (search.view) return { mode: "view", viewId: search.view };
  if (search.subject) return { mode: "subject", subjectId: search.subject };
  if (search.visit)
    return {
      mode: "visit",
      visitUuid: search.visit,
      roomUuid: search.room ?? null,
    };
  if (search.room) return { mode: "room", roomUuid: search.room };
  return { mode: "clinic" };
};
